/* import __COLOCATED_TEMPLATE__ from './out-of-stock-condition.hbs'; */
/* RESPONSIBLE TEAM: team-knowledge-foundations */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type KnowledgeHubService from 'embercom/services/knowledge-hub-service';
import type ImportSource from 'embercom/models/import-service/import-source';
import type {
  Condition,
  MultiConditionRules,
  LegacyCondition,
  ShopifyMetafieldDefinition,
} from 'embercom/models/import-service/import-source';

export type { MultiConditionRules, LegacyCondition, ShopifyMetafieldDefinition };
export type { Condition };

interface Signature {
  Args: {
    modal: any;
    importSource: ImportSource;
    onModalClose: () => void;
  };
  Element: null;
}

type LogicalOperator = 'and' | 'or';

interface SelectItem {
  text: string;
  value: string;
  isDisabled?: boolean;
  component?: string;
}

const STANDARD_FIELDS: Array<{ key: string; operators: string[] }> = [
  { key: 'sellable_online_quantity', operators: ['eq', 'ne', 'gt', 'lt', 'gte', 'lte', 'known'] },
  { key: 'available_for_sale', operators: ['eq'] },
  { key: 'inventory_policy', operators: ['eq', 'ne'] },
];

function emptyCondition(): Condition {
  return { field_type: 'standard', key: '', operator: '', value: null };
}

function encodeStandardKey(key: string): string {
  return `standard:${key}`;
}

function encodeMetafieldKey(def: ShopifyMetafieldDefinition): string {
  return `metafield:${def.namespace}:${def.key}:${def.type}`;
}

export default class ShopifyOutOfStockCondition extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare knowledgeHubService: KnowledgeHubService;
  @service declare notificationsService: any;

  @tracked logicalOperator: LogicalOperator = 'and';
  @tracked conditions: Condition[] = [emptyCondition()];
  @tracked isSaving = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    let stored = args.importSource?.outOfStockCondition;
    if (stored && 'conditions' in stored) {
      // New multi-condition format
      this.logicalOperator = stored.logical_operator ?? 'and';
      this.conditions = stored.conditions.map((c) => ({ ...c }));
    } else if (stored) {
      // Legacy { key, operator, value } — convert to single metafield row
      let legacy = stored;
      this.logicalOperator = 'and';
      this.conditions = [
        {
          field_type: 'metafield',
          namespace: 'custom',
          key: legacy.key,
          operator: legacy.operator === '==' ? 'eq' : 'ne',
          value: legacy.value,
        },
      ];
    }
  }

  get hasMultipleConditions(): boolean {
    return this.conditions.length > 1;
  }

  get logicalOperatorOptions(): SelectItem[] {
    return [
      {
        text: this.intl.t(
          'knowledge-hub.sync-modal.shopify.out-of-stock-condition.logical-operator-and',
        ),
        value: 'and',
      },
      {
        text: this.intl.t(
          'knowledge-hub.sync-modal.shopify.out-of-stock-condition.logical-operator-or',
        ),
        value: 'or',
      },
    ];
  }

  get availableFields(): SelectItem[] {
    let items: SelectItem[] = [];

    // Standard fields group
    for (let field of STANDARD_FIELDS) {
      items.push({
        text: this.intl.t(
          `knowledge-hub.sync-modal.shopify.out-of-stock-condition.field-${field.key.replace(/_/g, '-')}`,
        ),
        value: encodeStandardKey(field.key),
      });
    }

    // Metafields group
    let defs = this.args.importSource?.shopifyMetafieldDefinitions ?? [];
    if (defs.length > 0) {
      items.push({
        text: '──────────────',
        value: '__separator__',
        isDisabled: true,
      });
      for (let def of defs) {
        items.push({
          text: def.name || `${def.namespace}.${def.key}`,
          value: encodeMetafieldKey(def),
        });
      }
    }

    return items;
  }

  @action
  encodedKeyFor(condition: Condition): string | undefined {
    if (!condition.key) {
      return undefined;
    }
    if (condition.field_type === 'standard') {
      return encodeStandardKey(condition.key);
    }
    // metafield — prefer __type stored at selection time, fall back to definitions lookup
    let type = (condition as any).__type;
    if (!type) {
      let defs = this.args.importSource?.shopifyMetafieldDefinitions ?? [];
      let match = defs.find((d) => d.namespace === condition.namespace && d.key === condition.key);
      type = match?.type;
    }
    if (!type) {
      return undefined;
    }
    return `metafield:${condition.namespace}:${condition.key}:${type}`;
  }

  @action
  operatorsForField(encodedKey: string | undefined): SelectItem[] {
    if (!encodedKey) {
      return [];
    }
    let operators = this._operatorsForEncodedKey(encodedKey);
    return operators.map((op) => ({
      text: this.intl.t(`knowledge-hub.sync-modal.shopify.out-of-stock-condition.operator-${op}`),
      value: op,
    }));
  }

  @action
  valueOptionsForField(encodedKey: string | undefined): SelectItem[] | null {
    if (!encodedKey) {
      return null;
    }
    let parts = encodedKey.split(':');
    let fieldType = parts[0];

    if (fieldType === 'standard') {
      let key = parts[1];
      if (key === 'available_for_sale') {
        return [
          {
            text: this.intl.t('knowledge-hub.sync-modal.shopify.out-of-stock-condition.value-true'),
            value: 'true',
          },
          {
            text: this.intl.t(
              'knowledge-hub.sync-modal.shopify.out-of-stock-condition.value-false',
            ),
            value: 'false',
          },
        ];
      }
      if (key === 'inventory_policy') {
        return [
          {
            text: this.intl.t(
              'knowledge-hub.sync-modal.shopify.out-of-stock-condition.value-inventory-deny',
            ),
            value: 'DENY',
          },
          {
            text: this.intl.t(
              'knowledge-hub.sync-modal.shopify.out-of-stock-condition.value-inventory-continue',
            ),
            value: 'CONTINUE',
          },
        ];
      }
    }

    return null; // Free-text input
  }

  private get hasIncompleteCondition(): boolean {
    return this.conditions.some((c) => {
      if (!c.key) {
        return true;
      }
      if (!c.operator) {
        return true;
      }
      if (c.operator !== 'known' && (c.value === null || c.value === undefined || c.value === '')) {
        return true;
      }
      return false;
    });
  }

  get isSaveDisabled(): boolean {
    return this.isSaving || this.hasIncompleteCondition;
  }

  get isAddConditionDisabled(): boolean {
    return this.isSaving || this.hasIncompleteCondition;
  }

  @action
  addCondition() {
    this.conditions = [...this.conditions, emptyCondition()];
  }

  @action
  removeCondition(index: number) {
    let updated = [...this.conditions];
    updated.splice(index, 1);
    this.conditions = updated;
  }

  @action
  updateConditionField(index: number, encodedKey: string) {
    let updated = [...this.conditions];
    let condition = { ...updated[index]! };

    if (encodedKey === '__separator__' || !encodedKey) {
      return;
    }

    let parts = encodedKey.split(':');
    let fieldType = parts[0] as 'standard' | 'metafield';

    if (fieldType === 'standard') {
      condition.field_type = 'standard';
      condition.namespace = undefined;
      condition.key = parts[1] ?? '';
      delete (condition as any).__type;
    } else {
      // metafield:namespace:key:type
      condition.field_type = 'metafield';
      condition.namespace = parts[1] ?? '';
      condition.key = parts[2] ?? '';
      (condition as any).__type = parts[3] ?? '';
    }

    condition.operator = '';
    condition.value = null;
    updated[index] = condition;
    this.conditions = updated;
  }

  @action
  updateConditionOperator(index: number, operator: string) {
    let updated = [...this.conditions];
    let condition = { ...updated[index]! };
    condition.operator = operator;
    if (operator === 'known') {
      condition.value = null;
    }
    updated[index] = condition;
    this.conditions = updated;
  }

  @action
  updateConditionValue(index: number, value: string) {
    let updated = [...this.conditions];
    updated[index] = { ...updated[index]!, value };
    this.conditions = updated;
  }

  @action
  updateConditionValueFromEvent(index: number, event: InputEvent) {
    this.updateConditionValue(index, (event.target as HTMLInputElement).value);
  }

  @action
  updateLogicalOperator(op: string) {
    this.logicalOperator = op as LogicalOperator;
  }

  @action
  async saveCondition() {
    this.isSaving = true;
    try {
      // Strip internal __type field before sending to backend
      let cleanConditions = this.conditions.map((c) => {
        let { __type, ...rest } = c as any;
        void __type;
        return rest as Condition;
      });

      await this.knowledgeHubService.updateImportSourceWithParam(this.args.importSource.id, {
        shopify_context: {
          out_of_stock_condition: {
            logical_operator: this.logicalOperator,
            conditions: cleanConditions,
          },
        },
      });
      await this.args.importSource.reload();
      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.sync-modal.shopify.out-of-stock-condition.save-success'),
      );
      this.args.onModalClose();
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.shopify.out-of-stock-condition.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }

  @action
  async clearCondition() {
    this.isSaving = true;
    try {
      await this.knowledgeHubService.updateImportSourceWithParam(this.args.importSource.id, {
        shopify_context: {
          out_of_stock_condition: null,
        },
      });
      await this.args.importSource.reload();
      this.logicalOperator = 'and';
      this.conditions = [emptyCondition()];
      this.notificationsService.notifyConfirmation(
        this.intl.t('knowledge-hub.sync-modal.shopify.out-of-stock-condition.clear-success'),
      );
    } catch (e: unknown) {
      this.notificationsService.notifyError(
        this.intl.t('knowledge-hub.sync-modal.shopify.out-of-stock-condition.save-error'),
      );
    } finally {
      this.isSaving = false;
    }
  }

  private _operatorsForEncodedKey(encodedKey: string | undefined): string[] {
    if (!encodedKey) {
      return [];
    }
    let parts = encodedKey.split(':');
    let fieldType = parts[0];

    if (fieldType === 'standard') {
      let key = parts[1];
      return STANDARD_FIELDS.find((f) => f.key === key)?.operators ?? [];
    }

    // Metafield — allow text operators by default
    return ['eq', 'ne', 'known'];
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::SetupModal::SyncModal::Shopify::OutOfStockCondition': typeof ShopifyOutOfStockCondition;
    'knowledge-hub/setup-modal/sync-modal/shopify/out-of-stock-condition': typeof ShopifyOutOfStockCondition;
  }
}
